import { t } from '@lingui/core/macro';
import { useSuspenseQuery } from '@tanstack/react-query';
import { createFileRoute, useRouteContext } from '@tanstack/react-router';
import { useEffect } from 'react';
import { z } from 'zod';

import { notify } from '@/components/base/notifications/notifications';

import { CalendarConnectionPanel } from './-components/calendar-connection-panel';
import {
  SettingsSection,
  SettingsSectionSkeleton,
} from './-components/settings-section';
import { calendarConnectionQueryOptions } from './-utils/calendar-connection-query';
import { SETTINGS_HELP } from './-utils/help-articles';

/**
 * Set by the return trip from Google (see `googleConnectUrl`). The router
 * parses search values as JSON, so it can arrive as a boolean or a string.
 */
const calendarSearchSchema = z.object({
  calendarIntegration: z
    .union([z.boolean(), z.literal('true')])
    .optional()
    .catch(undefined),
});

export const Route = createFileRoute(
  '/researchers/_authenticated/settings/calendar',
)({
  validateSearch: calendarSearchSchema,
  loader: ({ context }) =>
    context.queryClient.ensureQueryData(
      calendarConnectionQueryOptions(context.user.id),
    ),
  pendingComponent: SettingsSectionSkeleton,
  component: CalendarConnectionsPage,
  head: () => ({ meta: [{ title: t`Calendar connections` }] }),
});

function CalendarConnectionsPage() {
  const { user } = Route.useRouteContext();
  const { env } = useRouteContext({ from: '__root__' });
  const { calendarIntegration } = Route.useSearch();
  const navigate = Route.useNavigate();
  const { data: connection } = useSuspenseQuery(
    calendarConnectionQueryOptions(user.id),
  );

  // The website sends the researcher back here whether or not Google was
  // linked (an account already linked to another user comes back unlinked),
  // so the outcome is read off the connection rather than the parameter.
  useEffect(() => {
    if (calendarIntegration === undefined) return;
    notify(
      connection.status === 'disconnected'
        ? {
            title: t`Google Calendar wasn't connected`,
            description: t`If that Google account is linked to another Respondent account, disconnect it there first.`,
            color: 'error',
          }
        : {
            title: t`Google Calendar connected`,
            description: t`Choose your calendars below.`,
            color: 'success',
          },
    );
    void navigate({ search: {}, replace: true });
  }, [calendarIntegration, connection.status, navigate]);

  return (
    <SettingsSection
      helpUrl={SETTINGS_HELP.calendarConnections}
      title={t`Calendar connections`}
      description={t`Link your calendar so Respondent can check for conflicts and add bookings to it.`}
    >
      <CalendarConnectionPanel
        connection={connection}
        userId={user.id}
        websiteUrl={env.WEBSITE_URL}
      />
    </SettingsSection>
  );
}
